/**
 * A dizimação do traço por linha de pixel de profundidade (onda 313, R9) — conta pura, sem DOM.
 *
 * Um CPTu tem ~2.000 leituras e a trilha tem ~500 px de altura: desenhar todas é desenhar três ou quatro
 * por pixel. Em cada linha de pixel ficam a primeira, a mínima, a máxima e a última leitura, na ordem
 * da profundidade (no máximo 4 pontos por pixel) — o pico de uma leitura só continua no traço, e a linha
 * passa pelos mesmos extremos que passaria com todas.
 *
 * É SÓ O DESENHO: o cursor, a leitura na cota, a tabela, o ábaco, a seleção, a estatística e as
 * sugestões contam todas as leituras, nunca as do traço.
 */

/**
 * O traço de uma série em profundidade, dizimado: `z` e `v` dos pontos que ficam, na ordem, e `quebras`
 * — os índices (nos pontos que ficam) em que a linha recomeça, porque um valor NaN a cortou entre o
 * ponto anterior e este (a leitura sem profundidade não tem lugar no eixo e fica de fora; o `Resultado`
 * já não a traz). Fora da janela [z0, z1] só fica a vizinha de cada lado, para a linha chegar à borda.
 * As linhas de pixel são `altura` faixas iguais da janela; a profundidade é suposta crescente.
 */
export function dizimarPorPixel(
  z: ArrayLike<number>,
  v: ArrayLike<number>,
  x: { z0: number; z1: number; altura: number },
): { z: Float64Array; v: Float64Array; quebras: number[] } {
  const n = Math.min(z.length, v.length);
  const altura = Math.max(1, Math.floor(x.altura));
  const passo = (x.z1 - x.z0) / altura;
  const linhaDe = (zz: number): number => (passo > 0 ? Math.floor((zz - x.z0) / passo) : 0);

  // A primeira leitura antes da janela e a primeira depois dela (as vizinhas de fora).
  let antes = -1;
  let depois = -1;
  for (let i = 0; i < n; i++) {
    const zz = z[i];
    if (!Number.isFinite(zz)) continue;
    if (zz < x.z0) antes = i;
    else if (zz > x.z1 && depois < 0) depois = i;
  }

  const saidaZ: number[] = [];
  const saidaV: number[] = [];
  const quebras: number[] = [];
  let cortado = false; // um NaN apareceu desde o último ponto guardado

  // O grupo da linha de pixel corrente: os índices da primeira, da mínima, da máxima e da última.
  let linha = Number.NaN;
  let primeira = -1;
  let minima = -1;
  let maxima = -1;
  let ultima = -1;
  const fechar = (): void => {
    if (primeira < 0) return;
    const indices = [primeira, minima, maxima, ultima].sort((a, b) => a - b);
    let anterior = -1;
    for (const k of indices) {
      if (k === anterior) continue;
      anterior = k;
      if (cortado && saidaZ.length > 0) quebras.push(saidaZ.length);
      cortado = false;
      saidaZ.push(z[k]);
      saidaV.push(v[k]);
    }
    primeira = -1;
  };

  for (let i = 0; i < n; i++) {
    const zz = z[i];
    const vv = v[i];
    const naJanela = zz >= x.z0 && zz <= x.z1;
    if (!naJanela && i !== antes && i !== depois) continue;
    if (!Number.isFinite(zz) || !Number.isFinite(vv)) {
      fechar();
      cortado = true;
      linha = Number.NaN;
      continue;
    }
    const l = naJanela ? linhaDe(zz) : (zz < x.z0 ? -1 : altura + 1);
    if (l !== linha || !naJanela) {
      fechar();
      linha = naJanela ? l : Number.NaN;
      primeira = i;
      minima = i;
      maxima = i;
      ultima = i;
      if (!naJanela) fechar();
      continue;
    }
    if (vv < v[minima]) minima = i;
    if (vv > v[maxima]) maxima = i;
    ultima = i;
  }
  fechar();
  return { z: Float64Array.from(saidaZ), v: Float64Array.from(saidaV), quebras };
}
